import { useEffect, useState } from "react";
import axios from "axios";
import { format, isBefore, isWeekend, startOfToday } from "date-fns";
import { it } from "date-fns/locale";
import { CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { SITE } from "@/constants/testIds";
import { SLOT_NOTE } from "@/content";
import { ContactFields, emptyContact } from "./ContactFields";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ids = {
  name: SITE.apptName, email: SITE.apptEmail, phone: SITE.apptPhone,
  area: SITE.apptArea, message: SITE.apptMessage, privacy: SITE.apptPrivacy,
};

const SlotPicker = ({ slots, booked, time, setTime }) => (
  <div className="grid grid-cols-3 gap-2">
    {slots.map((t) => {
      const taken = booked.includes(t);
      const active = t === time;
      return (
        <button
          key={t}
          type="button"
          disabled={taken}
          onClick={() => setTime(t)}
          data-testid={SITE.slotBtn(t)}
          className={`h-11 rounded-full text-sm font-semibold border transition-colors ${
            active ? "bg-navy-900 text-white border-navy-900"
              : taken ? "border-navy-100 text-navy-200 line-through cursor-not-allowed"
              : "border-navy-200 text-navy-900 hover:border-navy-900"
          }`}
        >
          {t}
        </button>
      );
    })}
  </div>
);

export const AppointmentForm = () => {
  const [day, setDay] = useState();
  const [avail, setAvail] = useState({ slots: [], booked: [] });
  const [time, setTime] = useState("");
  const [form, setForm] = useState(emptyContact);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    if (!day) return;
    setTime("");
    axios.get(`${API}/appointments/availability`, { params: { date: format(day, "yyyy-MM-dd") } })
      .then((r) => setAvail(r.data))
      .catch(() => toast.error("Impossibile caricare gli orari. Riprova."));
  }, [day]);

  const submit = async (e) => {
    e.preventDefault();
    if (!day || !time) return toast.error("Seleziona giorno e orario dell'appuntamento.");
    if (!form.privacy) return toast.error("È necessario accettare l'informativa privacy.");
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/appointments`, { ...form, date: format(day, "yyyy-MM-dd"), time });
      setDone(data);
      toast.success("Appuntamento richiesto con successo.");
    } catch (err) {
      toast.error(err.response?.data?.detail?.toString?.() || "Invio non riuscito. Riprova o chiama lo studio.");
    } finally {
      setSending(false);
    }
  };

  if (done) {
    return (
      <div data-testid={SITE.apptSuccess} className="py-16 text-center">
        <CheckCircle2 className="mx-auto h-12 w-12 text-bronze" strokeWidth={1.4} />
        <h3 className="mt-6 text-2xl font-semibold text-navy-900">Grazie, {done.name}.</h3>
        <p className="mt-3 text-navy-500">
          Abbiamo ricevuto la tua richiesta per {format(new Date(done.date), "EEEE d MMMM", { locale: it })} alle {done.time}.
          Lo studio ti contatterà per confermare.
        </p>
        <Button variant="outline" onClick={() => { setDone(null); setDay(undefined); setForm(emptyContact); }} className="mt-8 rounded-full border-navy-900 text-navy-900">
          Nuova prenotazione
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="grid lg:grid-cols-12 gap-10">
      <div className="lg:col-span-5">
        <p className="text-sm font-semibold text-navy-900">1. Scegli il giorno</p>
        <div data-testid={SITE.calendar} className="mt-4 rounded-2xl border border-navy-100 inline-block bg-white">
          <Calendar
            mode="single"
            locale={it}
            weekStartsOn={1}
            selected={day}
            onSelect={setDay}
            disabled={(d) => isBefore(d, startOfToday()) || isWeekend(d)}
            className="p-4"
          />
        </div>
        <p className="mt-3 text-xs text-navy-500">{SLOT_NOTE}</p>
        <p className="mt-8 text-sm font-semibold text-navy-900">2. Scegli l'orario</p>
        <div className="mt-4">
          {day ? (
            <SlotPicker slots={avail.slots} booked={avail.booked} time={time} setTime={setTime} />
          ) : (
            <p data-testid={SITE.slotsEmpty} className="text-sm text-navy-500 border border-dashed border-navy-200 p-4">Seleziona prima un giorno dal calendario.</p>
          )}
        </div>
      </div>
      <div className="lg:col-span-7">
        <p className="text-sm font-semibold text-navy-900">3. I tuoi dati</p>
        <div data-testid={SITE.selectedSummary} className="mt-4 mb-6 rounded-xl bg-navy-50 border-l-4 border-bronze px-4 py-3 text-sm text-navy-900">
          {day && time ? `${format(day, "EEEE d MMMM yyyy", { locale: it })} · ore ${time}` : "Nessun orario selezionato"}
        </div>
        <ContactFields value={form} onChange={setForm} ids={ids} />
        <Button type="submit" disabled={sending} data-testid={SITE.apptSubmit} className="mt-8 w-full sm:w-auto rounded-full bg-navy-900 hover:bg-navy-700 h-14 px-8 font-semibold tracking-wide transition-colors">
          {sending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Richiedi l'appuntamento
        </Button>
      </div>
    </form>
  );
};
